import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getRoleLabel, isOrgAdminRole } from '../utils/roleUtils';

import { formatDate, formatDateTime } from '../utils/dateFormat';
import OrganizationsTable, { planBadge } from './OrganizationsTable';
// How a role was given or taken, in words (the same wording as System Admin's Role history)
const ROLE_CHANGE_REASONS = {
  ASSIGNED: 'Given by an admin',
  REMOVED: 'Taken by an admin',
  REGISTERED: 'Registered the organization',
  SELF_ASSIGNED: 'Took Org Admin (only member)',
  ACCOUNT_MANAGER_ASSIGNED: 'Account Manager assigned',
  ACCOUNT_MANAGER_ENDED: 'Account Manager assignment ended',
  LEFT_ORGANIZATION: 'Removed from the organization'
};

const when = (iso) => (iso ? formatDateTime(iso) : '—');
const day = (iso) => (iso ? formatDate(iso) : '—');

/**
 * The Organizations section as Tech Support sees it: find an organization and look at its people, plan, usage and role history,
 * and email a locked-out person a set-password link. Nothing here changes an organization, its plan, users or roles: those are
 * System Admin's.
 */
function SupportOrganizations({ organizations, selected, onSelect }) {
  const [details, setDetails] = useState(null);
  const [roleChanges, setRoleChanges] = useState([]);
  const [sending, setSending] = useState(null);

  const load = useCallback(async (id) => {
    setDetails(null);
    try {
      const [d, r] = await Promise.all([
        axios.get(`/api/system-admin/organizations/${id}/details`),
        axios.get(`/api/system-admin/organizations/${id}/role-changes`).catch(() => ({ data: [] }))
      ]);
      setDetails(d.data);
      setRoleChanges(Array.isArray(r.data) ? r.data : []);
    } catch (error) {
      toast.error('Could not load the organization');
    }
  }, []);

  useEffect(() => {
    if (selected) load(selected.id);
  }, [selected, load]);

  const sendLink = async (user) => {
    if (!window.confirm(`Email ${user.email} a link to choose their password? The link goes to their own address.`)) return;
    setSending(user.id);
    try {
      const { data } = await axios.post(`/api/system-admin/organizations/${selected.id}/users/${user.id}/send-set-password-link`);
      toast.success(data?.message || 'Link sent');
    } catch (error) {
      const msg = error.response?.data;
      toast.error(typeof msg === 'string' && msg ? msg : 'Could not send the link');
    } finally {
      setSending(null);
    }
  };

  const resetTwoFactor = async (user) => {
    if (!window.confirm(`Reset two-step sign-in for ${user.email}? Only after you have checked that it really is them asking (for example by calling the number we have for their organization). They set it up again at their next sign-in if their organization requires it.`)) return;
    setSending(user.id);
    try {
      const { data } = await axios.post(`/api/system-admin/organizations/${selected.id}/users/${user.id}/reset-two-factor`);
      toast.success(data?.message || 'Reset');
    } catch (error) {
      const msg = error.response?.data;
      toast.error(typeof msg === 'string' && msg ? msg : 'Could not reset it');
    } finally {
      setSending(null);
    }
  };

  if (!selected) {
    return (
      <div className="mt-3">
        <OrganizationsTable organizations={organizations} openLabel="View" onOpen={(o) => onSelect({ id: o.id, name: o.name })} />
      </div>
    );
  }

  const org = details?.organization;
  const usage = details?.usageMetrics;
  const limits = details?.subscriptionUsage;
  return (
    <div className="mt-3">
      <Button variant="outline-secondary" className="mb-3" onClick={() => onSelect(null)}>← Back to Organizations</Button>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{selected.name}</h5>
          <span className="small text-muted">A look only: plans, users and roles are changed by a System Admin</span>
        </Card.Header>
        <Card.Body>
          {!details ? (
            <div className="d-flex justify-content-center py-4"><Spinner animation="border" /></div>
          ) : (
            <Tabs defaultActiveKey="users">
              <Tab eventKey="users" title={`Users (${details.userCount ?? (details.users || []).length})`}>
                <p className="text-muted small mt-3">
                  For someone who is locked out or never set a password: <strong>Email set-password link</strong> sends them a link to choose one.
                  It goes to their own address. If they lost their phone and recovery codes for two-step sign-in, <strong>Reset two-step</strong> clears it
                  (check who is asking first; the reset is recorded with your name).
                </p>
                <Table striped bordered hover responsive>
                  <thead><tr><th>Name</th><th>Email</th><th>Roles</th><th>Status</th><th /></tr></thead>
                  <tbody>
                    {(details.users || []).map((u) => (
                      <tr key={u.id}>
                        <td>{u.firstName} {u.lastName}</td>
                        <td>{u.email}</td>
                        <td>
                          {(u.roles || []).length === 0 ? <span className="text-muted">No roles</span> : (u.roles || []).map((role, i) => {
                            const name = typeof role === 'string' ? role : role.name;
                            return <Badge key={i} bg={isOrgAdminRole(name) ? 'primary' : 'secondary'} className="me-1">{getRoleLabel(typeof role === 'object' ? role : name)}</Badge>;
                          })}
                        </td>
                        <td>{u.isActive !== false ? <Badge bg="success">Active</Badge> : <Badge bg="danger">Inactive</Badge>}</td>
                        <td className="text-end">
                          <Button size="sm" variant="outline-primary" disabled={u.isActive === false || sending === u.id} onClick={() => sendLink(u)}>
                            {sending === u.id ? 'Sending...' : 'Email set-password link'}
                          </Button>
                          <Button size="sm" variant="outline-danger" className="ms-2" disabled={sending === u.id} onClick={() => resetTwoFactor(u)}>
                            Reset two-step
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Tab>

              <Tab eventKey="plan" title="Plan">
                <Row className="mt-3">
                  <Col md={7}>
                    <Card className="mb-3">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <div className="text-muted small">Current Plan</div>
                            <h5 className="mb-0">{org?.subscriptionPlan?.name || 'No plan assigned (unlimited)'}</h5>
                          </div>
                          {planBadge(org)}
                        </div>
                        <p className="text-muted small">Plan start: {day(org?.subscriptionStartDate)}  ·  Plan end: {day(org?.subscriptionEndDate)}</p>
                        {limits && (
                          <Table size="sm" bordered className="mb-0">
                            <tbody>
                              {[['Warehouses', limits.warehouseCount, org?.subscriptionPlan?.maxWarehouses],
                                ['Production units', limits.productionUnitCount, org?.subscriptionPlan?.maxProductionUnits],
                                ['Retail stores', limits.retailStoreCount, org?.subscriptionPlan?.maxRetailStores],
                                ['Transactions today', limits.todayTransactionCount, org?.subscriptionPlan?.maxDailyTransactions],
                                ['Users', limits.activeUserCount, org?.subscriptionPlan?.maxUsers]].map(([label, used, max]) => (
                                  <tr key={label}><td>{label}</td><td>{used}{max != null ? ` of ${max}` : ''}</td></tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={5}>
                    <Card>
                      <Card.Header><h6 className="mb-0">Plan History</h6></Card.Header>
                      <Card.Body>
                        {(details.subscriptionHistory || []).length === 0 ? <span className="text-muted small">No changes yet.</span> : (
                          details.subscriptionHistory.map((entry, idx) => (
                            <div key={entry.id} className={idx > 0 ? 'pt-2 mt-2 border-top' : ''}>
                              <div className="small text-muted">{when(entry.changedAt)}</div>
                              <div className="small">Plan: <strong>{entry.fromPlan?.name || 'No plan'}</strong> &rarr; <strong>{entry.toPlan?.name || 'No plan'}</strong></div>
                              <div className="small">Status: {entry.fromStatus || '—'} &rarr; {entry.toStatus || '—'}</div>
                              <div className="small text-muted">by {entry.changedByName || entry.changedByEmail}</div>
                            </div>
                          ))
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>

              <Tab eventKey="usage" title="Usage Metrics">
                {usage && (
                  <Row className="mt-3 g-3">
                    <Col md={6}>
                      <h6>Setup &amp; master data</h6>
                      <Table size="sm" bordered>
                        <tbody>
                          {[['Warehouses', usage.warehouseCount], ['Production units', usage.productionUnitCount], ['Retail stores', usage.retailStoreCount],
                            ['Active users', usage.activeUserCount], ['Products', usage.productCount], ['Materials', usage.materialCount], ['Services', usage.serviceCount]].map(([l, v]) => (
                            <tr key={l}><td>{l}</td><td className="text-end">{v}</td></tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={6}>
                      <h6>Activity &amp; recency</h6>
                      <Table size="sm" bordered>
                        <tbody>
                          <tr><td>Total GRNs</td><td className="text-end">{usage.grnCount}</td></tr>
                          <tr><td>Total GINs</td><td className="text-end">{usage.ginCount}</td></tr>
                          <tr><td>Transactions today</td><td className="text-end">{usage.todayTransactionCount}</td></tr>
                          <tr><td>Last login (any user)</td><td className="text-end">{when(usage.lastLoginAt)}</td></tr>
                          <tr><td>Last GRN date</td><td className="text-end">{usage.lastGrnDate ? formatDate(`${usage.lastGrnDate}T00:00:00`) : '—'}</td></tr>
                          <tr><td>Last GIN date</td><td className="text-end">{usage.lastGinDate ? formatDate(`${usage.lastGinDate}T00:00:00`) : '—'}</td></tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                )}
              </Tab>

              <Tab eventKey="role-history" title="Role history">
                {roleChanges.length === 0 ? <p className="text-muted mt-3">No role changes recorded yet.</p> : (
                  <Table size="sm" bordered responsive className="mt-3">
                    <thead><tr><th>When</th><th>User</th><th>Role</th><th>Change</th><th>How</th><th>By</th></tr></thead>
                    <tbody>
                      {roleChanges.map((c) => (
                        <tr key={c.id}>
                          <td className="small text-nowrap">{when(c.changedAt)}</td>
                          <td className="small">{c.userEmail}</td>
                          <td className="small">{getRoleLabel(c.roleName)}</td>
                          <td><Badge bg={c.action === 'ADDED' ? 'success' : 'danger'}>{c.action === 'ADDED' ? 'Given' : 'Taken'}</Badge></td>
                          <td className="small">{ROLE_CHANGE_REASONS[c.reason] || c.reason}</td>
                          <td className="small">{c.changedBy === 'system' ? 'iBigDo (automatic)' : c.changedBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>
            </Tabs>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default SupportOrganizations;
